// Public directory (annuaire) — localized URL slugs + UI strings + labels.
// FR + EN are curated inline; the other 20 languages are LLM-generated in
// directory_i18n_gen.js (DIR_GEN) and merged below. Placeholders {v}/{n} are
// interpolated by dl()/cl().
import { DIR_GEN } from "@/i18n/directory_i18n_gen";

export const DIRECTORY_SLUGS = {
  fr: "fermes", en: "farms", es: "granjas", it: "fattorie", de: "hoefe",
  nl: "boerderijen", sv: "gardar", da: "gaarde", bg: "fermi", el: "farmes",
  hr: "farme", et: "talud", fi: "tilat", hu: "farmok", lv: "saimniecibas",
  lt: "ukiai", pl: "gospodarstwa", pt: "quintas", ro: "ferme", sk: "farmy",
  sl: "kmetije", cs: "farmy",
};

export const dirPath = (lang) => (lang === "fr" ? "/fermes" : `/${lang}/${DIRECTORY_SLUGS[lang] || "farms"}`);

export const SALE_MODE_KEYS = ["direct", "market", "delivery", "pickup", "online", "phone", "email"];

const SALE_LABELS = {
  fr: { direct: "Vente directe", market: "Marché", delivery: "Livraison", pickup: "Retrait sur place", online: "Boutique en ligne", phone: "Commande par téléphone", email: "Commande par email" },
  en: { direct: "Direct sales", market: "Market", delivery: "Delivery", pickup: "On-site pickup", online: "Online shop", phone: "Order by phone", email: "Order by email" },
  ...Object.fromEntries(Object.entries(DIR_GEN).map(([lg, d]) => [lg, d.sale])),
};

export const saleModeLabel = (lang, key) => (SALE_LABELS[lang] || SALE_LABELS.en)[key] || (SALE_LABELS.en)[key] || key;

export const PRECISION_KEYS = ["exact", "approx", "city", "region", "hidden"];
const PRECISION_LABELS = {
  fr: { exact: "Adresse exacte", approx: "Adresse approximative", city: "Ville uniquement", region: "Région uniquement", hidden: "Masquer la localisation" },
  en: { exact: "Exact address", approx: "Approximate address", city: "City only", region: "Region only", hidden: "Hide location" },
  ...Object.fromEntries(Object.entries(DIR_GEN).map(([lg, d]) => [lg, d.precision])),
};
export const precisionLabel = (lang, key) => (PRECISION_LABELS[lang] || PRECISION_LABELS.en)[key] || key;

const UI = {
  fr: {
    title: "Découvrez les fermes et éleveurs",
    subtitle: "Découvrez les fermes et éleveurs qui utilisent Ma Petite Ferme pour gérer leur activité.",
    searchPh: "Rechercher une ferme, une ville, un produit…",
    country: "Pays", animal: "Type d'élevage", sale: "Mode de vente", all: "Tous",
    mapView: "Voir sur la carte", listView: "Voir en liste",
    discover: "Découvrir la ferme", verified: "Ferme vérifiée", uses: "Utilise Ma Petite Ferme",
    noResults: "Aucune ferme trouvée pour cette recherche.",
    prev: "Précédent", next: "Suivant", results: "ferme(s)",
    ctaTitle: "Vous êtes éleveur ?", ctaText: "Créez votre page publique gratuite et rejoignez le réseau des fermes européennes.",
    ctaBtn: "Gérer ma ferme avec Ma Petite Ferme",
    secAbout: "À propos", secAnimals: "Notre élevage", secSale: "Vente & distribution",
    secGallery: "Galerie", secContact: "Contact", secLocation: "Localisation",
    report: "Signaler cette page", website: "Site internet",
    edTitle: "Ma page publique — Annuaire des fermes", edSub: "Rejoignez le réseau public des fermes Ma Petite Ferme.",
    edPublish: "Publier ma ferme dans l'annuaire Ma Petite Ferme",
    edPublishHint: "Votre page sera accessible publiquement et indexable par les moteurs de recherche. Seules les informations que vous activez seront visibles. Désactivable à tout moment.",
    edSlogan: "Slogan", edRegion: "Région", edPrecision: "Localisation publique",
    edAnimals: "Types d'élevage", edSaleModes: "Modes de vente", edWebsite: "Site internet",
    edContactVis: "Visibilité des coordonnées", edGallery: "Galerie (jusqu'à 4 photos)",
    edShowPhone: "Afficher mon téléphone", edShowEmail: "Afficher mon email",
    edShowWebsite: "Afficher mon site internet", edShowSocial: "Afficher mes réseaux sociaux",
    edAutoNote: "Votre profil devient public dès qu'il est complet (description + type d'élevage + logo ou photo). Il apparaîtra alors dans l'annuaire.",
    joinTitle: "Rejoignez l'annuaire des fermes", joinText: "Créez votre page publique gratuite et gagnez en visibilité auprès des consommateurs.", joinBtn: "Créer ma page publique",
    edDigest: "Recevoir le récap mensuel par e-mail", edDigestHint: "Un e-mail par mois avec le nombre de vues et de clics de votre page publique. Désactivable à tout moment.",
    navDirectory: "Fermes",
  },
  en: {
    title: "Discover farms & producers",
    subtitle: "Discover the farms and producers who use Ma Petite Ferme to run their business.",
    searchPh: "Search a farm, a city, a product…",
    country: "Country", animal: "Livestock type", sale: "Sale mode", all: "All",
    mapView: "View on map", listView: "View as list",
    discover: "Discover farm", verified: "Verified farm", uses: "Uses Ma Petite Ferme",
    noResults: "No farm found for this search.",
    prev: "Previous", next: "Next", results: "farm(s)",
    ctaTitle: "Are you a farmer?", ctaText: "Create your free public page and join the European network of farms.",
    ctaBtn: "Manage my farm with Ma Petite Ferme",
    secAbout: "About", secAnimals: "Our livestock", secSale: "Sales & distribution",
    secGallery: "Gallery", secContact: "Contact", secLocation: "Location",
    report: "Report this page", website: "Website",
    edTitle: "My public page — Farm directory", edSub: "Join the public network of Ma Petite Ferme farms.",
    edPublish: "Publish my farm in the Ma Petite Ferme directory",
    edPublishHint: "Your page will be publicly accessible and indexable by search engines. Only the information you enable will be visible. Can be disabled anytime.",
    edSlogan: "Slogan", edRegion: "Region", edPrecision: "Public location",
    edAnimals: "Livestock types", edSaleModes: "Sale modes", edWebsite: "Website",
    edContactVis: "Contact visibility", edGallery: "Gallery (up to 4 photos)",
    edShowPhone: "Show my phone", edShowEmail: "Show my email",
    edShowWebsite: "Show my website", edShowSocial: "Show my social links",
    edAutoNote: "Your profile becomes public as soon as it is complete (description + livestock type + logo or photo). It will then appear in the directory.",
    joinTitle: "Join the farm directory", joinText: "Create your free public page and gain visibility with consumers.", joinBtn: "Create my public page",
    edDigest: "Receive the monthly recap by email", edDigestHint: "One email per month with the number of views and clicks on your public page. Can be disabled anytime.",
    navDirectory: "Farms",
  },
  ...Object.fromEntries(Object.entries(DIR_GEN).map(([lg, d]) => [lg, d.ui])),
};

const _interp = (s, args) => (typeof s === "string" ? s.replace(/\{[vn]\}/g, () => (args[0] != null ? args[0] : "")) : s);

export const dl = (lang, key, ...args) => _interp((UI[lang] || UI.en)[key] || UI.en[key] || key, args);

// --- Directory SEO category pages (country / region / type) -----------------
// Type slug prefix per language (mirror of backend TYPE_PREFIX). Country/region
// slugs are language-independent (proper nouns), only the type prefix differs.
export const TYPE_PREFIX = {
  fr: "elevage", en: "livestock", es: "ganaderia", it: "allevamento", de: "tierhaltung",
  nl: "veeteelt", sv: "djurhallning", da: "husdyrhold", bg: "zhivotnovadstvo", el: "ktinotrofia",
  hr: "stocarstvo", et: "loomakasvatus", fi: "kotielaimet", hu: "allattartas", lv: "lopkopiba",
  lt: "gyvulininkyste", pl: "hodowla", pt: "pecuaria", ro: "crestere-animale", sk: "chov",
  sl: "zivinoreja", cs: "chov-zvirat",
};

export const dirSlugify = (s) =>
  (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();

export const catSlug = (lang, kind, value) =>
  kind === "type" ? `${TYPE_PREFIX[lang] || TYPE_PREFIX.en}-${value}` : dirSlugify(value);

export const catPath = (lang, kind, value) => `${dirPath(lang)}/${catSlug(lang, kind, value)}`;

// Localized product page path (fr = no prefix). Shop nav route stays /boutique/{farm}.
export const shopProductPath = (lang, farmSlug, pSlug) => `${lang === "fr" ? "" : "/" + lang}/boutique/${farmSlug}/${pSlug}`;

const CAT_UI = {
  fr: {
    browse: "Parcourir l'annuaire", byCountry: "Par pays", byRegion: "Par région", byType: "Par type d'élevage",
    back: "← Retour à l'annuaire", results: "ferme(s)",
    titleType: "Fermes {v} en Europe", titleCountry: "Fermes en {v}", titleRegion: "Fermes en {v}",
    descType: "Découvrez les fermes et éleveurs {v} qui vendent en direct sur Ma Petite Ferme.",
    descCountry: "Découvrez les fermes et producteurs locaux en {v} sur l'annuaire Ma Petite Ferme.",
    descRegion: "Découvrez les fermes et producteurs locaux en {v} sur l'annuaire Ma Petite Ferme.",
    dirStatsTitle: "Ma page publique", dirStatsViews: "Vues du profil", dirStatsWeb: "Clics site web", dirStatsPhone: "Clics téléphone", dirStats30d: "30 derniers jours",
    pdpBack: "← Retour à la ferme", pdpOther: "Autres produits de la ferme", pdpInStock: "En stock", pdpOut: "Rupture de stock", pdpBy: "Vendu par", pdpHome: "Accueil",
    share: "Partager", copied: "Lien copié !",
    favLogin: "Se connecter", favRegister: "Créer un compte", favName: "Nom", favEmail: "E-mail", favPassword: "Mot de passe",
    favToRegister: "Pas encore de compte ? S'inscrire", favToLogin: "Déjà inscrit ? Se connecter", favWelcome: "Bienvenue !",
    favTitle: "Mes fermes favorites", favEmpty: "Aucune ferme en favori pour l'instant. Explorez l'annuaire et cliquez sur le cœur ❤", favLoginPrompt: "Connectez-vous pour enregistrer et retrouver vos fermes favorites.", favPwdHint: "6 caractères minimum.",
    fulfillTitle: "Où récupérer vos produits près de chez vous",
    fulfillPickup: "{n} ferme(s) proposent le retrait sur place",
    fulfillDelivery: "{n} ferme(s) livrent à domicile",
    fulfillPoints: "Points de retrait & villes",
    titleCity: "Fermes à {v}", descCity: "Découvrez les fermes et producteurs locaux à {v} qui vendent en direct sur Ma Petite Ferme.", byCity: "Par ville",
    faqTitle: "Questions fréquentes",
    relatedTitle: "Explorez aussi",
  },
  en: {
    browse: "Browse the directory", byCountry: "By country", byRegion: "By region", byType: "By livestock type",
    back: "← Back to the directory", results: "farm(s)",
    titleType: "{v} farms in Europe", titleCountry: "Farms in {v}", titleRegion: "Farms in {v}",
    descType: "Discover {v} farms and producers selling directly on Ma Petite Ferme.",
    descCountry: "Discover local farms and producers in {v} on the Ma Petite Ferme directory.",
    descRegion: "Discover local farms and producers in {v} on the Ma Petite Ferme directory.",
    dirStatsTitle: "My public page", dirStatsViews: "Profile views", dirStatsWeb: "Website clicks", dirStatsPhone: "Phone clicks", dirStats30d: "Last 30 days",
    pdpBack: "← Back to the farm", pdpOther: "More products from this farm", pdpInStock: "In stock", pdpOut: "Out of stock", pdpBy: "Sold by", pdpHome: "Home",
    share: "Share", copied: "Link copied!",
    favLogin: "Sign in", favRegister: "Create account", favName: "Name", favEmail: "Email", favPassword: "Password",
    favToRegister: "No account yet? Sign up", favToLogin: "Already registered? Sign in", favWelcome: "Welcome!",
    favTitle: "My favorite farms", favEmpty: "No favorite farm yet. Explore the directory and tap the heart ❤", favLoginPrompt: "Sign in to save and find your favorite farms.", favPwdHint: "At least 6 characters.",
    fulfillTitle: "Where to get your products near you",
    fulfillPickup: "{n} farm(s) offer on-site pickup",
    fulfillDelivery: "{n} farm(s) deliver to your door",
    fulfillPoints: "Pickup points & towns",
    titleCity: "Farms in {v}", descCity: "Discover local farms and producers in {v} selling directly on Ma Petite Ferme.", byCity: "By town",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Explore more",
  },
  ...Object.fromEntries(Object.entries(DIR_GEN).map(([lg, d]) => [lg, d.cat])),
};

export const cl = (lang, key, ...args) => _interp((CAT_UI[lang] || CAT_UI.en)[key] ?? CAT_UI.en[key] ?? key, args);
